import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createCodeChallenge, generateNonce } from "@services";
import { hasValidEntropy } from "@services/crypto";
import { RootState } from "@store";
import { generateCodeVerifier } from "oslo/oauth2";

import { retriveAccessAndRefreshToken, refreshAccessToken } from "@services/Zettle";
import { ErrorCategory, ErrorSeverity, handleThunkError, createError, logError } from '@services/errorHandling';

export enum ZettleAuthStatus {
    Unauthenticated,
    GeneratingChallenge,
    WaitingForCode,
    RequestedToken,
    Authenticated,
    RefreshingToken,
    Error,
}

export interface ZettleAuthState {
    status: ZettleAuthStatus;
    token?: string;
    refresh_token?: string;
    expires_at?: number; // Unix timestamp in milliseconds
    redirect_uri?: string;
    code_verifier?: string;
    code_challenge?: string;
    nonce?: string;
    error?: string;
}

export const initialState = {
    status: ZettleAuthStatus.Unauthenticated,
} as ZettleAuthState;

export const zettleAuthSlice = createSlice({
    name: "zettleAuth",
    initialState: initialState,
    reducers: {
        logout: () => {
            // Reset to initial unauthenticated state
            return initialState;
        },
        restore: (_state, action: PayloadAction<ZettleAuthState>) => {
            // Restore state from encrypted storage
            return action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initializeAuthentication.pending, (state) => {
                state.status = ZettleAuthStatus.GeneratingChallenge;
            })
            .addCase(initializeAuthentication.fulfilled, (state, action: PayloadAction<AuthenticationData>) => {
                state.code_challenge = action.payload.code_challenge;
                state.code_verifier = action.payload.code_verifier;

                // Generate nonce and verify it has sufficient entropy for CSRF protection
                const nonce = generateNonce();
                if (!hasValidEntropy(nonce, 32)) {
                    const error = createError(
                        ErrorCategory.SECURITY,
                        ErrorSeverity.CRITICAL,
                        "Generated nonce has insufficient entropy for CSRF protection",
                        undefined,
                        { nonceLength: nonce.length }
                    );
                    logError(error);
                    throw new Error(error.message);
                }
                state.nonce = nonce;
                state.status = ZettleAuthStatus.WaitingForCode;
            })
            .addCase(exchangeCodeForToken.pending, (state) => {
                state.status = ZettleAuthStatus.RequestedToken;
            })
            .addCase(exchangeCodeForToken.fulfilled, (state, action) => {
                state.token = action.payload.access_token;
                state.refresh_token = action.payload.refresh_token;

                // Calculate expiration time (expires_in is in seconds)
                // Subtract 5 minutes as buffer to refresh before actual expiration
                const expiresInMs = (action.payload.expires_in || 7200) * 1000;
                const bufferMs = 5 * 60 * 1000; // 5 minutes
                state.expires_at = Date.now() + expiresInMs - bufferMs;

                state.status = ZettleAuthStatus.Authenticated;
            })
            .addCase(exchangeCodeForToken.rejected, (state, action) => {
                // Error already logged in thunk via handleThunkError
                state.error = action.error.message;
                state.status = ZettleAuthStatus.Error;
            })
            .addCase(refreshToken.pending, (state) => {
                state.status = ZettleAuthStatus.RefreshingToken;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                // Update tokens with refreshed values
                state.token = action.payload.access_token;
                state.refresh_token = action.payload.refresh_token;

                // Calculate new expiration time
                const expiresInMs = (action.payload.expires_in || 7200) * 1000;
                const bufferMs = 5 * 60 * 1000; // 5 minutes
                state.expires_at = Date.now() + expiresInMs - bufferMs;

                state.status = ZettleAuthStatus.Authenticated;
            })
            .addCase(refreshToken.rejected, (state, action) => {
                // Error already logged in thunk via handleThunkError
                // If refresh fails, logout the user
                state.error = typeof action.payload === 'string' ? action.payload : "Token refresh failed";
                state.status = ZettleAuthStatus.Error;
                // Clear tokens since refresh failed
                state.token = undefined;
                state.refresh_token = undefined;
                state.expires_at = undefined;
            });
    },
});

interface AuthenticationData {
    code_verifier: string;
    code_challenge: string;
}

export const initializeAuthentication = createAsyncThunk(
    "zettleAuth/initializeAuthentication",
    async () => {
        const code_verifier = generateCodeVerifier();
        return createCodeChallenge(code_verifier).then((code_challenge: string) => {
            return {
                code_verifier,
                code_challenge,
            };
        });
    },
    {
        condition: (_code, { getState }) => {
            const state = getState() as typeof RootState;
            return ![ZettleAuthStatus.WaitingForCode, ZettleAuthStatus.GeneratingChallenge].includes(
                state.zettleAuth.status,
            );
        },
    },
);

export const exchangeCodeForToken = createAsyncThunk(
    "zettleAuth/exchangeCodeForToken",
    async (code: string, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as typeof RootState;
            if (state.token) return null;
            const response = await retriveAccessAndRefreshToken(code, state.zettleAuth.code_verifier);
            return response.data;
        } catch (error) {
            const message = handleThunkError(
                error,
                ErrorCategory.AUTH,
                'Failed to exchange authorization code for token',
                { code }
            );
            return thunkAPI.rejectWithValue(message);
        }
    },
    {
        condition: (_code, { getState }) => {
            const state = getState() as typeof RootState;
            return state.zettleAuth.status !== ZettleAuthStatus.RequestedToken;
        },
    },
);

export const refreshToken = createAsyncThunk(
    "zettleAuth/refreshToken",
    async (_, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as typeof RootState;
            const refresh_token = state.zettleAuth.refresh_token;

            if (!refresh_token) {
                return thunkAPI.rejectWithValue("No refresh token available");
            }

            const response = await refreshAccessToken(refresh_token);
            return response.data;
        } catch (error) {
            const state = thunkAPI.getState() as typeof RootState;
            const message = handleThunkError(
                error,
                ErrorCategory.AUTH,
                'Token refresh failed',
                { hasRefreshToken: !!state.zettleAuth.refresh_token }
            );
            return thunkAPI.rejectWithValue(message);
        }
    },
    {
        condition: (_, { getState }) => {
            const state = getState() as typeof RootState;
            // Don't refresh if already refreshing or not authenticated
            return state.zettleAuth.status !== ZettleAuthStatus.RefreshingToken &&
                   state.zettleAuth.status !== ZettleAuthStatus.Unauthenticated;
        },
    },
);

export const selectZettleAuth = (state: typeof RootState) => state.zettleAuth;
export const selectStatus = (state: typeof RootState) =>
    state ? (state.zettleAuth ? state.zettleAuth.status : null) : null;
export const selectZettleAuthToken = (state: typeof RootState) =>
    state ? (state.zettleAuth ? state.zettleAuth.token : null) : null;
export const selectNonce = (state: typeof RootState) =>
    state ? (state.zettleAuth ? state.zettleAuth.nonce : null) : null;
export const selectCodeVerifier = (state: typeof RootState) =>
    state ? (state.zettleAuth ? state.zettleAuth.code_verifier : null) : null;
export const selectCodeChallenge = (state: typeof RootState) =>
    state ? (state.zettleAuth ? state.zettleAuth.code_challenge : null) : null;
export const selectError = (state: typeof RootState) => state?.zettleAuth?.error;
export const selectExpiresAt = (state: typeof RootState) => state?.zettleAuth?.expires_at;

/**
 * Checks if the current token is expired
 */
export const selectIsTokenExpired = (state: typeof RootState): boolean => {
    const expiresAt = selectExpiresAt(state);
    if (!expiresAt) return true;
    return Date.now() >= expiresAt;
};

export const { logout, restore } = zettleAuthSlice.actions;
export const zettleAuthReducer = zettleAuthSlice.reducer;
